// RiskCenterPage — User-facing risk dashboard
// Shows: daily loss left, max DD remaining, consistency tracker, news lockouts, margin usage
// Mirrors admin "Risk Flag Dashboard" but from user perspective

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TfCard, TfCardFloating, TfSectionHeaderView, TfStatRow, TfProgress } from '../../components/TfPrimitives';

interface RiskMetric {
  id: string;
  label: string;
  used: number;
  total: number;
  unit: string;
  status: 'safe' | 'warning' | 'critical';
  description: string;
  icon: string;
  color: string;
}

const METRICS: RiskMetric[] = [
  { id: 'm1', label: 'Daily loss',          used: 280,   total: 2000,  unit: '$',  status: 'safe',     description: 'Funded accounts auto-flatten at 2% daily loss. Below 80% = safe.',         icon: '📉', color: '#34C759' },
  { id: 'm2', label: 'Max drawdown',        used: 1200,  total: 3000,  unit: '$',  status: 'safe',     description: 'Trails until it reaches your initial balance, then fixed at that level.', icon: '⏳', color: '#34C759' },
  { id: 'm3', label: 'Consistency (today)', used: 1200,  total: 3000,  unit: '$',  status: 'safe',     description: 'No single day may exceed 50% of target (funded). 40% during eval.',      icon: '🎯', color: '#34C759' },
  { id: 'm4', label: 'Margin per trade',    used: 11,    total: 30,    unit: '%',  status: 'safe',     description: 'Section 13.13: max 30% of available margin per single trade idea.',         icon: '🛡️', color: '#34C759' },
  { id: 'm5', label: 'Open positions',      used: 3,     total: 10,    unit: '',   status: 'safe',     description: 'Mid tier allows 10 Standard contracts. 1 Standard = 10 Micros.',         icon: '📊', color: '#34C759' },
];

const FLAGS = [
  { id: 'f1', severity: 'info',    title: 'Profitable streak: 3 days',     desc: "You're on track to unlock your first reward. Keep going!",                       time: '2h ago' },
  { id: 'f2', severity: 'warning', title: 'EOD 15:55 CT in 32 min',        desc: 'Auto-flatten window approaching. Close manually or let it close.',                time: '5m ago' },
  { id: 'f3', severity: 'info',    title: 'News event in 2h 14m',          desc: 'FOMC Chair Powell speaks 14:00 ET. Bot will auto-pause 2 min before.',           time: '10m ago' },
  { id: 'f4', severity: 'success', title: 'Daily Pause passed yesterday',  desc: 'Closed 2 positions cleanly. Daily loss limit reset for today.',                  time: '1d ago' },
];

const SEV_META: Record<string, { color: string; icon: string; bg: string }> = {
  info:    { color: '#007AFF', icon: 'ℹ️', bg: 'var(--tf-blue-tint)' },
  warning: { color: '#FF9500', icon: '⚠️', bg: 'var(--tf-orange-tint)' },
  success: { color: '#34C759', icon: '✓',  bg: 'var(--tf-green-tint)' },
  error:   { color: '#FF3B30', icon: '⛔', bg: 'var(--tf-red-tint)' },
};

export function RiskCenterPage() {
  const nav = useNavigate();
  const [metrics] = useState(METRICS);

  return (
    <div className="min-h-screen bg-tf-surface-1 pb-8">
      <div className="sticky top-0 z-10 flex h-11 items-center justify-between border-b border-tf-border ios26-navbar px-2 backdrop-blur-xl">
        <button onClick={() => nav(-1)} data-testid="back" className="flex h-9 w-9 items-center justify-center text-tf-blue haptic">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M15 5l-7 7 7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        <div className="absolute left-1/2 -translate-x-1/2 text-[15px] font-semibold">Risk center</div>
        <div className="w-9" />
      </div>

      {/* Hero — risk score + pill buttons */}
      <div className="mx-4 mt-4">
        <TfCardFloating className="text-center">
          <div className="text-[13px]" style={{ color: '#8E8E93' }}>Risk score</div>
          <div className="mt-1 flex items-baseline justify-center gap-1">
            <span className="text-[40px] font-bold tabular-nums leading-none text-tf-profit">87</span>
            <span className="text-[20px] font-bold text-tf-text-3">/100</span>
          </div>
          <div className="mt-1 flex items-center justify-center gap-1 text-[13px]">
            <span className="text-tf-text-3">Healthy · 5 metrics monitored</span>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-3">
            <button onClick={() => nav('/portal/trading-rules')} className="flex h-12 items-center justify-center gap-1.5 rounded-full bg-tf-text-1 text-[15px] font-semibold text-white active:scale-[0.97] transition-transform">
              Rules
              <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M7 17L17 7" /><path d="M7 7h10v10" />
              </svg>
            </button>
            <button onClick={() => alert('Kill switch activated. All open positions closed.')} className="flex h-12 items-center justify-center gap-1.5 rounded-full bg-tf-text-1 text-[15px] font-semibold text-white active:scale-[0.97] transition-transform">
              Kill switch
              <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 7L7 17" /><path d="M17 17H7V7" />
              </svg>
            </button>
          </div>
          <div className="mt-3 inline-flex items-center gap-1 rounded-full bg-tf-green-tint px-3 py-1.5 text-[11px] font-semibold text-tf-profit">
            🟢 All systems nominal
          </div>
        </TfCardFloating>
      </div>

      {/* 3-up stat row */}
      <TfStatRow
        className="mt-4"
        stats={[
          { label: 'Score', value: '87/100', color: '#34C759' },
          { label: 'Metrics', value: '5', color: '#1C1C1E' },
          { label: 'Flags', value: '4', color: '#007AFF' },
        ]}
      />

      {/* Metrics */}
      <TfSectionHeaderView title="Metrics" viewAllLabel="View all" onViewAll={() => {}} />
      <div className="mx-4">
        <TfCard>
          {metrics.map((m, i) => {
            const pct = (m.used / m.total) * 100;
            return (
              <div key={m.id} data-testid={`metric-${m.id}`} className={['px-4 py-3', i < metrics.length - 1 ? 'border-b border-tf-border' : ''].join(' ')}>
                <div className="flex items-center gap-2.5 mb-1.5">
                  <span className="text-[18px]">{m.icon}</span>
                  <span className="text-[14px] font-bold text-tf-text-1 flex-1">{m.label}</span>
                  <span className="text-[14px] font-bold tabular-nums" style={{ color: m.color }}>{m.unit === '$' ? '$' : ''}{m.used.toLocaleString()}{m.unit === '%' ? '%' : ''}{m.unit === '$' ? '' : ` / ${m.total}`}</span>
                </div>
                <TfProgress value={pct} color={m.color} />
                <p className="text-[11px] text-tf-text-3 mt-1.5">{m.description}</p>
              </div>
            );
          })}
        </TfCard>
      </div>

      {/* Recent flags */}
      <TfSectionHeaderView title="Recent activity" viewAllLabel="View all" onViewAll={() => {}} />
      <div className="mx-4 space-y-2">
        {FLAGS.map((f) => {
          const m = SEV_META[f.severity];
          return (
            <div key={f.id} data-testid={`flag-${f.id}`} className="rounded-[12px] bg-white p-3 flex items-start gap-2.5" style={{ boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}>
              <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[14px]" style={{ backgroundColor: m.bg }}>{m.icon}</div>
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <h3 className="text-[13px] font-semibold text-tf-text-1">{f.title}</h3>
                  <span className="text-[10px] text-tf-text-3">{f.time}</span>
                </div>
                <p className="text-[11px] text-tf-text-3 mt-0.5">{f.desc}</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Quick links */}
      <TfSectionHeaderView title="Risk tools" />
      <div className="mx-4 grid grid-cols-2 gap-2">
        {[
          { label: 'Margin monitor',  sub: 'Real-time margin',     icon: '🛡️', test: 'tool-margin' },
          { label: 'Auto-flatten',    sub: 'EOD timer',             icon: '⏰', test: 'tool-flatten' },
          { label: 'News blackouts',  sub: 'Event calendar',        icon: '📰', test: 'tool-news' },
          { label: 'Trading rules',   sub: 'Section 13',            icon: '📋', test: 'tool-rules' },
          { label: 'Daily Pause',     sub: 'EOD flatten',           icon: '⏸', test: 'tool-daily-pause' },
          { label: 'Kill switch',     sub: 'Emergency stop',        icon: '🛑', test: 'tool-kill-switch' },
        ].map((t) => (
          <button
            key={t.test}
            onClick={() => {
              if (t.test === 'tool-margin') nav('/portal/margin');
              if (t.test === 'tool-flatten') nav('/portal/auto-flatten');
              if (t.test === 'tool-news') nav('/portal/news-blackouts');
              if (t.test === 'tool-rules') nav('/portal/trading-rules');
              if (t.test === 'tool-daily-pause') nav('/portal/daily-pause');
              if (t.test === 'tool-kill-switch') alert('Kill switch activated. All open positions closed.');
            }}
            data-testid={t.test}
            className="rounded-[12px] bg-white p-3 text-left active:opacity-40"
            style={{ boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}
          >
            <div className="text-[20px]">{t.icon}</div>
            <div className="mt-1 text-[13px] font-semibold text-tf-text-1">{t.label}</div>
            <div className="text-[10px] text-tf-text-3">{t.sub}</div>
          </button>
        ))}
      </div>
    </div>
  );
}
